<script setup lang="ts">
import { watch, computed, ref } from 'vue'
import Socials from './Socials.vue'

const props = defineProps({
    locale: {
        type: String,
        default: 'en'
    },
    cdnUrl: {
        type: String,
        default: ''
    },
    isDesktop: {
        type: Boolean,
        default: true
    }
})

const businessLogoPath = ref(`${props.cdnUrl}/images/business-light.svg`)
const partnersLogoPath = ref(`${props.cdnUrl}/images/partners-light.svg`)

const helpUrl = computed(() => {
    switch (props.locale) {
        case 'fr':
            return 'https://support.chargemap.com/l/fr'
        case 'de':
            return 'https://support.chargemap.com/l/de'
        case 'es':
            return 'https://support.chargemap.com/l/es'
        default:
            return 'https://support.chargemap.com/l/en'
    }
})

const blogUrl = computed(() => {
    switch (props.locale) {
        case 'fr':
            return 'https://blog.chargemap.com/fr'
        case 'de':
            return 'https://blog.chargemap.com/de'
        default:
            return 'https://blog.chargemap.com'
    }
})

const businessUrl = computed(() => {
    let language = ''

    switch (props.locale) {
        case 'en':
        case 'de':
            language = `/${props.locale}`
    }

    return `https://chargemap-business.com${language}`
})

const partnersUrl = computed(() => {
    let language = ''

    switch (props.locale) {
        case 'en':
        case 'de':
            language = `/${props.locale}`
    }

    return `https://chargemap-partners.com${language}`
})

const jobsUrl = 'https://chargemap.welcomekit.co/'

const showServices = ref(false)

const toggleServices = () => {
    showServices.value = !showServices.value
}

const showHelp = ref(false)

const toggleHelp = () => {
    showHelp.value = !showHelp.value
}

const showAbout = ref(false)

const toggleAbout = () => {
    showAbout.value = !showAbout.value
}

watch(
    () => props.isDesktop,
    (newValue: boolean) => {
        showServices.value = newValue
        showHelp.value = newValue
        showAbout.value = newValue
    },
    {
        immediate: true
    }
)
</script>

<template>
    <div class="secondary-footer">
        <div class="flex flex-col items-start gap-2 pb-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t('word.services') }}
            </span>
            <a
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                href="#"
                @click.prevent="toggleServices"
            >
                {{ $t('word.services') }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showServices && 'rotate-180']"
                />
            </a>
            <div
                class="flex-col items-start gap-2"
                :class="[showServices ? 'flex' : 'hidden']"
            >
                <a class="link" href="/map" :title="$t('footer.map')">
                    {{ $t('footer.map') }}
                </a>
                <a class="link" href="/mobile" :title="$t('footer.mobile')">
                    {{ $t('footer.mobile') }}
                </a>
                <a
                    class="link"
                    href="/mobile/planner"
                    :title="$t('footer.itinerary')"
                >
                    {{ $t('footer.itinerary') }}
                </a>
                <a class="link" href="/pass" :title="$t('footer.pass')">
                    {{ $t('footer.pass') }}
                </a>
            </div>
        </div>
        <div class="flex flex-col items-start gap-2 py-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t('word.help') }}
            </span>
            <a
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                href="#"
                @click.prevent="toggleHelp"
            >
                {{ $t('word.help') }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showHelp && 'rotate-180']"
                />
            </a>
            <div
                class="flex flex-col items-start gap-2"
                :class="[showHelp ? 'flex' : 'hidden']"
            >
                <a class="link" :href="helpUrl" :title="$t('word.faq')">
                    {{ $t('word.faq') }}
                </a>
                <a class="link" href="/support" :title="$t('footer.contact')">
                    {{ $t('footer.contact') }}
                </a>
            </div>
        </div>
        <div class="flex flex-col items-start gap-2 py-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t('word.about') }}
            </span>
            <a
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                href="#"
                @click.prevent="toggleAbout"
            >
                {{ $t('word.about') }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showAbout && 'rotate-180']"
                />
            </a>
            <div
                class="flex flex-col items-start gap-2"
                :class="[showAbout ? 'flex' : 'hidden']"
            >
                <a class="link" href="/about" :title="$t('footer.who')">
                    {{ $t('footer.who') }}
                </a>
                <a class="link" :href="jobsUrl" :title="$t('footer.join')">
                    {{ $t('footer.join') }}
                </a>
                <a class="link" :href="blogUrl" :title="$t('word.blog')">
                    {{ $t('word.blog') }}
                </a>
            </div>
        </div>
        <div
            class="pt-4 md:py-0 flex flex-col lg:flex-row items-center lg:justify-evenly gap-6 lg:gap-2 lg:w-1/2"
        >
            <a
                class="flex flex-col items-center md:items-start gap-1 whitespace-nowrap"
                :href="businessUrl"
                title="Chargemap Business"
                target="_blank"
            >
                <img :src="businessLogoPath" alt="Chargemap Business" />
                <span
                    class="text-blue-500 text-xs lg:text-sm font-bold leading-none focus:no-underline"
                >
                    {{ $t('footer.business') }}
                </span>
            </a>
            <a
                class="flex flex-col items-center md:items-start gap-1 whitespace-nowrap"
                :href="partnersUrl"
                title="Chargemap Partners"
                target="_blank"
            >
                <img :src="partnersLogoPath" alt="Chargemap Partners" />
                <span
                    class="text-violet-500 text-xs lg:text-sm font-bold leading-none focus:no-underline"
                >
                    {{ $t('footer.partners') }}
                </span>
            </a>

            <div class="flex md:hidden">
                <Socials :locale />
            </div>
        </div>
    </div>
</template>

<style scoped>
.secondary-footer {
  @apply flex bg-transparent flex-col divide-y-[1px] divide-zinc-200 md:divide-y-0 md:flex-row justify-between mx-4 md:px-6 md:mx-0 lg:px-[50px] py-5;
}
.secondary-footer .accordion-button {
  @apply text-sky-700 hover:text-sky-500 active:text-sky-700 text-[15px] md:text-[13px] lg:text-[15px] font-bold focus:no-underline transition-all duration-300;
}
.secondary-footer .link {
  @apply flex text-zinc-700 dark:text-zinc-200 hover:text-sky-400 active:text-sky-700 active:font-semibold disabled:text-zinc-400 whitespace-nowrap text-ellipsis text-[13px] lg:text-[15px];
}
</style>
